import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import type { AnswerLetter, SheetAnswer } from '../../../api/types';
import { Badge } from '../../../components/ui';

const LETTERS: AnswerLetter[] = ['A', 'B', 'C', 'D'];
// Same "unclear" threshold as the extractor's own review flag (see
// QuestionsReview.tsx's initExpanded) so the two screens agree on what
// "low confidence" means.
export const LOW_CONFIDENCE = 0.6;

interface AnswerStripProps {
  /** Number of bubbled rows on this exam (`SheetDetail.n_rows`). */
  nRows: number;
  /** `SheetDetail.answers` — row (as string key "1".."n") -> letter or null. */
  answers: Record<string, SheetAnswer | null>;
  /** `SheetDetail.row_confidence`. */
  rowConfidence: Record<string, number>;
  /** Rows the reader saw two bubbles filled on (from the `multi:` warning). */
  multiRows: Set<number>;
  onChange: (row: number, letter: AnswerLetter | null) => void;
  disabled?: boolean;
  /** `SheetDetail.row_numbers`: the question number printed beside each row. */
  rowNumbers?: Record<string, number>;
}

/**
 * Rows 1..n with A/B/C/D/blank toggle buttons (plan B Task 6). One row is
 * "active" for keyboard input: ArrowUp/Down move it, A-D set its answer,
 * Backspace/0 blanks it. Escape is intentionally left unhandled here so it
 * bubbles to Modal's own document-level listener and closes the drawer.
 */
export default function AnswerStrip({
  nRows, answers, rowConfidence, multiRows, onChange, disabled = false, rowNumbers,
}: AnswerStripProps) {
  const [activeRow, setActiveRow] = useState(1);
  const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    setActiveRow((r) => Math.min(Math.max(r, 1), Math.max(nRows, 1)));
  }, [nRows]);

  useEffect(() => {
    rowRefs.current[activeRow]?.scrollIntoView?.({ block: 'nearest' });
  }, [activeRow]);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (disabled || nRows === 0) return;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveRow((r) => Math.max(1, r - 1));
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveRow((r) => Math.min(nRows, r + 1));
      return;
    }
    const letter = e.key.length === 1 ? (e.key.toUpperCase() as AnswerLetter) : null;
    if (letter && LETTERS.includes(letter)) {
      e.preventDefault();
      onChange(activeRow, letter);
      return;
    }
    if (e.key === 'Backspace' || e.key === '0') {
      e.preventDefault();
      onChange(activeRow, null);
    }
  }

  const rows = Array.from({ length: nRows }, (_, i) => i + 1);

  return (
    <div
      role="group"
      aria-label="Answers"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="max-h-80 space-y-1 overflow-y-auto rounded-[12px] border border-line bg-surface p-2 outline-none focus-visible:ring-2 focus-visible:ring-accent">
      {rows.map((row) => {
        const key = String(row);
        const current = answers[key] ?? null;
        const confidence = rowConfidence[key] ?? 1;
        const isMulti = multiRows.has(row);
        const isLow = !isMulti && confidence < LOW_CONFIDENCE;
        const isActive = row === activeRow;
        return (
          <div
            key={row}
            ref={(el) => { rowRefs.current[row] = el; }}
            onClick={() => setActiveRow(row)}
            className={`flex flex-wrap items-center gap-2 rounded-[10px] px-2 py-1 ${
              isActive ? 'bg-surface-2' : ''
            }`}>
            <span className="w-8 shrink-0 font-mono text-xs text-muted">#{rowNumbers?.[key] ?? row}</span>
            <div className="flex gap-1">
              {LETTERS.map((letter) => (
                <button
                  key={letter}
                  type="button"
                  disabled={disabled}
                  aria-pressed={current === letter}
                  aria-label={`Row ${row} answer ${letter}`}
                  onClick={() => { setActiveRow(row); onChange(row, letter); }}
                  className={`flex h-9 w-9 items-center justify-center rounded-full border text-sm font-semibold transition-colors disabled:opacity-50 ${
                    current === letter
                      ? 'border-navy bg-navy text-white dark:border-mist-fixed dark:bg-mist-fixed dark:text-navy'
                      : 'border-line-strong text-ink hover:border-accent'
                  }`}>
                  {letter}
                </button>
              ))}
              <button
                type="button"
                disabled={disabled}
                aria-pressed={current == null}
                aria-label={`Row ${row} blank`}
                onClick={() => { setActiveRow(row); onChange(row, null); }}
                className={`flex h-9 w-9 items-center justify-center rounded-full border text-xs font-semibold transition-colors disabled:opacity-50 ${
                  current == null
                    ? 'border-muted bg-surface-2 text-muted'
                    : 'border-line-strong text-muted hover:border-muted'
                }`}>
                &mdash;
              </button>
            </div>
            {isMulti && <Badge tone="danger" size="sm">Marked twice</Badge>}
            {isLow && <Badge tone="warning" size="sm">Unclear</Badge>}
          </div>
        );
      })}
      {nRows === 0 && (
        <p className="px-2 py-4 text-center text-sm text-muted">No bubbled questions on this exam.</p>
      )}
    </div>
  );
}
